<route lang="yaml">
  meta:
    layout: escuela-taller-home
    header: true
  </route>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Content, fetchOneEntry, isPreviewing } from '@builder.io/sdk-vue';
import { useRoute, useRouter } from 'vue-router';
import { registeredComponents } from '@/init-builder.js';
import { useAuthStore } from "@/stores/auth";
import { BUILDER_API_KEY } from '@/config/builder';
import strapiService from '@/services/strapi';
import moment from 'moment';
// Importar moment-es
import 'moment/dist/locale/es';
import Talleres from '@/components/builder/Talleres.vue';
import axios from 'axios';
import NavbarTalleres from '@/components/escuela/NavbarTalleres.vue';
import HeaderTalleres from '@/components/escuela/HeaderTalleres.vue';
import { useTalleresStore } from '@/stores/talleres';
import { ChevronRightIcon, ArrowDownTrayIcon } from '@heroicons/vue/24/solid'; // Importar iconos de Heroicons
import Papa from 'papaparse';
moment.locale('es');


// State
const content = ref(null);
const canShowContent = ref(false);
const isLoading = ref(true);
const isContentReady = ref(false);
const talleres = ref([]);
const isLoadingTalleres = ref(true);
const totalAlumnosPorTaller = ref({});
const totalAnecdotasPorTaller = ref({});
const estadisticasPorTaller = ref({});
const isLoadingStats = ref({});
const talleresDocente = ref([]);
const novedad = ref(null);
const isLoadingNovedad = ref(false);
const datosExcel = ref([]);

// Mapeo de slugs a URLs de imágenes de Shopify
const shopifyImages = {
'taller-1': 'https://cdn.shopify.com/s/files/1/0884/1638/7346/files/1_1virtual-shopify_hernan_casciari.webp?v=1746810761',
'taller-2': 'https://cdn.shopify.com/s/files/1/0884/1638/7346/files/1_1virtual-shopify_hernan_casciari.webp?v=1746810761',
'taller-marzo': 'https://cdn.shopify.com/s/files/1/0884/1638/7346/files/1_1virtual-shopify_hernan_casciari.webp?v=1746810761'
};

// Store and Route
const router = useRouter();
const route = useRoute();
const store = useAuthStore();
const talleresStore = useTalleresStore();
const usuario = computed(() => store.usuario);
const esUsuario = computed(() => !!usuario.value);
const esDocente = computed(() => !!usuario.value?.docente);

// Variable de configuración para controlar la visibilidad del certificado
const mostrarCertificado = ref(false);

const getRegisteredComponents = () => {
return registeredComponents;
};

// Cargar talleres del docente
const cargarTalleresDocente = async () => {
  try {
    isLoadingTalleres.value = true;
    const email = usuario.value?.email;

    if (!email) {
      console.log('No hay email de usuario disponible');
      return;
    }

    console.log('Intentando obtener talleres para docente con email:', email);
    const response = await axios.get(`https://staging-strapi.orsai.org/api/docentes?filters[email][$eqi]=${email}`);
    console.log('Respuesta completa de la API:', response.data);
    
    if (response.data?.data?.[0]?.talleres) {
      console.log('Talleres encontrados:', response.data.data[0].talleres);
      talleresDocente.value = response.data.data[0].talleres.map(taller => {
        let image = taller.imagenPortada?.formats?.large?.url || 
                   taller.imagenPortada?.url || 
                   taller.agenda?.[0]?.thumbnail || 
                   "https://placehold.co/600x400";

        // Obtener la fecha de la primera clase ordenando el array de agenda
        let primeraClase;
        if (taller.agenda?.length) {
          const clasesOrdenadas = [...taller.agenda].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
          primeraClase = new Date(clasesOrdenadas[0].fecha);
        } else {
          primeraClase = new Date('9999-12-31');
        }

        const ruta = `/virtuales/${taller.docente?.toLowerCase().replace(' ', '-')}/${taller.slug}/inicio`;
        
        // Actualizar las estadísticas para este taller
        totalAlumnosPorTaller.value[taller.id] = taller.totalAlumnos || 0;
        totalAnecdotasPorTaller.value[taller.id] = taller.totalAnecdotas || 0;
        
        // Inicializar estadísticas generales para este taller
        estadisticasPorTaller.value[taller.id] = {
          alumnos: taller.totalAlumnos || 0,
          cuentos: taller.totalAnecdotas || 0,
          cuentos_con_audio: taller.totalAnecdotasConAudio || 0,
          cuentos_con_resumenes: taller.totalAnecdotasConResumenes || 0,
          perfiles_completos: taller.totalAlumnosPerfilesCompletos || 0,
          meta: []
        };

        // Obtener el total de alumnos desde el Excel si está disponible
        const totalAlumnosExcel = obtenerTotalAlumnosDesdeExcel(taller.docente);

        const tallerProcesado = {
          ...taller,
          id: taller.id,
          titulo: taller.titulo,
          docente: taller.docente,
          imagen: image,
          ruta,
          fechaPrimeraClase: primeraClase,
          progreso: calcularProgresoTaller(taller),
          etapa: taller.etapa,
          totalAlumnosExcel: totalAlumnosExcel // Agregar el total desde Excel
        };
        
        // Debug específico para talleres de Birmajer
        if (taller.docente?.toLowerCase().includes('birmajer')) {
          console.log(`🔍 DEBUG BIRMAJER - Taller: ${taller.titulo}`);
          console.log(`🔍 DEBUG BIRMAJER - Slug: ${taller.slug}`);
          console.log(`🔍 DEBUG BIRMAJER - Total desde Strapi: ${taller.totalAlumnos || 0}`);
          console.log(`🔍 DEBUG BIRMAJER - Total desde Excel (NO USADO): ${totalAlumnosExcel}`);
        }
        
        console.log('Taller procesado:', tallerProcesado);
        return tallerProcesado;
      }).sort((a, b) => {
        // El taller-1 y taller-2 siempre van al final, con taller-1 siendo el más viejo (último)
        if (a.slug === 'taller-1') return 1;
        if (b.slug === 'taller-1') return -1;
        if (a.slug === 'taller-2') return 1;
        if (b.slug === 'taller-2') return -1;

        // Para el resto de talleres, ordenar por fecha de primera clase, del más viejo al más próximo
        if (a.fechaPrimeraClase && b.fechaPrimeraClase) {
          return b.fechaPrimeraClase - a.fechaPrimeraClase;
        }
        // Si solo uno tiene fecha, poner el que tiene fecha primero
        if (a.fechaPrimeraClase) return -1;
        if (b.fechaPrimeraClase) return 1;
        // Si ninguno tiene fecha, mantener el orden por ID
        return a.id - b.id;
      });
      console.log('Array final de talleres procesados:', talleresDocente.value);
    } else {
      console.log('No se encontraron talleres para el docente');
    }
  } catch (error) {
    console.error('Error al cargar talleres del docente:', error);
    talleresDocente.value = [];
  } finally {
    isLoadingTalleres.value = false;
  }
};

// Cargar talleres del usuario
const cargarTalleres = async () => {
  try {
    isLoadingTalleres.value = true;
    const idUsuario = usuario.value?.id;
    console.log('Cargando talleres para usuario:', {
      idUsuario,
      email: usuario.value?.email,
      esDocente: usuario.value?.docente
    });

    // Si el usuario es docente, cargamos sus talleres como docente
    if (usuario.value?.docente) {
      console.log('Usuario es docente, cargando talleres como docente');
      await cargarTalleresDocente();
    }

    // Para todos los usuarios (incluyendo docentes), cargar talleres como alumno
    if (!idUsuario) {
      console.log('No hay ID de usuario, no se pueden cargar talleres como alumno');
      // Si es docente, ya tenemos sus talleres como docente
      if (usuario.value?.docente) {
        talleres.value = talleresDocente.value;
      }
      return;
    }

    console.log('Obteniendo talleres para alumno...');
    const response = await strapiService.getTalleresByUsuario(idUsuario, usuario.value?.email);
    console.log('Respuesta de talleres del alumno:', response);

    // Inicializar el estado de loading para cada taller
    response?.forEach(taller => {
      isLoadingStats.value[taller.id] = true;
    });

    // Cargar los talleres como alumno
    const talleresComoAlumno = response?.map(taller => {
      console.log('Procesando taller como alumno:', {
        id: taller.id,
        titulo: taller.titulo,
        slug: taller.slug,
        docente: taller.docente,
        progreso: calcularProgresoTaller(taller)
      });
      let image, ruta;
      ruta = `/virtuales/${taller.docente?.toLowerCase().replace(' ', '-')}/${taller.slug}/inicio`;

      // Usar la imagen de Shopify si existe, sino usar la imagen del backend o una imagen por defecto
      image = taller.imagenPortada?.formats?.large?.url || taller.imagenPortada?.url || taller.agenda?.[0]?.thumbnail || "https://placehold.co/600x400";

      // Obtener la fecha de la primera clase ordenando el array de agenda
      let primeraClase;
      if (taller.agenda?.length) {
        const clasesOrdenadas = [...taller.agenda].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
        primeraClase = new Date(clasesOrdenadas[0].fecha);
      } else {
        primeraClase = new Date('9999-12-31');
      }
      
      const hoy = new Date();
      const yaPaso = primeraClase < hoy;
      
      // Agregar mes y año para ordenamiento
      const mes = primeraClase.getMonth();
      const anio = primeraClase.getFullYear();
      const mesAnio = anio * 100 + mes; // Crear un número único para mes/año (ej: 202403 para marzo 2024)

      // Obtener el total de alumnos desde el Excel si está disponible
      const totalAlumnosExcel = obtenerTotalAlumnosDesdeExcel(taller.docente);
      
      const tallerProcesado = {
        ...taller,
        id: taller.id,
        titulo: taller.titulo,
        docente: taller.docente,
        imagen: image,
        ruta,
        progreso: calcularProgresoTaller(taller),
        etapa: taller.etapa,
        fechaPrimeraClase: primeraClase,
        yaPaso: yaPaso,
        mesAnio: mesAnio,
        alumno: taller.alumno,
        totalAlumnosExcel: totalAlumnosExcel // Agregar el total desde Excel
      };
      
      // Debug específico para talleres de Birmajer
      if (taller.docente?.toLowerCase().includes('birmajer')) {
        console.log(`🔍 DEBUG BIRMAJER (ALUMNO) - Taller: ${taller.titulo}`);
        console.log(`🔍 DEBUG BIRMAJER (ALUMNO) - Slug: ${taller.slug}`);
        console.log(`🔍 DEBUG BIRMAJER (ALUMNO) - Total desde Strapi: ${taller.totalAlumnos || 0}`);
        console.log(`🔍 DEBUG BIRMAJER (ALUMNO) - Total desde Excel (NO USADO): ${totalAlumnosExcel}`);
      }
      
      return tallerProcesado;
    }).sort((a, b) => {
      // El taller-1 y taller-2 siempre van al final, con taller-1 siendo el más viejo (último)
      if (a.slug === 'taller-1') return 1;
      if (b.slug === 'taller-1') return -1;
      if (a.slug === 'taller-2') return 1;
      if (b.slug === 'taller-2') return -1;

      // Ordenar por fecha de primera clase, del más nuevo al más viejo
      if (a.fechaPrimeraClase && b.fechaPrimeraClase) {
        return b.fechaPrimeraClase - a.fechaPrimeraClase;
      }

      // Si solo uno tiene fecha, poner el que tiene fecha primero
      if (a.fechaPrimeraClase) return -1;
      if (b.fechaPrimeraClase) return 1;

      // Si ninguno tiene fecha, mantener el orden por ID
      return a.id - b.id;
    });

    // Asignar talleres según el tipo de usuario
    if (usuario.value?.docente) {
      // Si es docente, combinar talleres como docente y como alumno
      talleres.value = [...(talleresDocente.value || []), ...(talleresComoAlumno || [])];
    } else {
      // Si no es docente, solo talleres como alumno
      talleres.value = talleresComoAlumno || [];
    }

    response?.forEach(async taller => {
      try {
        const statsResponse = await strapiService.get(`/api/statistics/general/${taller.slug}`);
        const stats = statsResponse.data;

        // Verificar que stats existe y tiene la estructura esperada
        if (!stats || !stats.general) {
          console.warn(`No se encontraron estadísticas para el taller ${taller.slug}`);
          throw new Error('Estructura de datos inválida');
        }

        // Mapear las estadísticas según el formato de la respuesta
        estadisticasPorTaller.value[taller.id] = {
          alumnos: stats.general.alumnos || 0,
          cuentos: stats.general.cuentos || 0,
          cuentos_con_audio: stats.general.cuentos_con_audio || 0,
          cuentos_con_resumenes: stats.general.cuentos_con_resumenes || 0,
          cuentos_con_votos_resumenes: stats.general.cuentos_con_votos_resumenes || 0,
          cuentos_con_votos_cuentos: stats.general.cuentos_con_votos_cuentos || 0,
          cuentos_con_votos_audios: stats.general.cuentos_con_votos_audios || 0,
          alumnos_votaron_resumenes: stats.general.alumnos_votaron_resumenes || 0,
          alumnos_votaron_cuentos: stats.general.alumnos_votaron_cuentos || 0,
          alumnos_votaron_audios: stats.general.alumnos_votaron_audios || 0,
          votos_resumenes: stats.general.votos_resumenes || 0,
          votos_lecturas: stats.general.votos_lecturas || 0,
          votos_audios: stats.general.votos_audios || 0,
          perfiles_completos: stats.general.perfiles_completos || 0,
          meta: stats.meta || []
        };

        totalAlumnosPorTaller.value[taller.id] = stats.general.alumnos || 0;
        totalAnecdotasPorTaller.value[taller.id] = stats.general.cuentos || 0;

      } catch (error) {
        console.error(`Error al cargar estadísticas para el taller ${taller.slug}:`, error);
        estadisticasPorTaller.value[taller.id] = {
          alumnos: 0,
          cuentos: 0,
          cuentos_con_audio: 0,
          cuentos_con_resumenes: 0,
          cuentos_con_votos_resumenes: 0,
          cuentos_con_votos_cuentos: 0,
          cuentos_con_votos_audios: 0,
          alumnos_votaron_resumenes: 0,
          alumnos_votaron_cuentos: 0,
          alumnos_votaron_audios: 0,
          votos_resumenes: 0,
          votos_lecturas: 0,
          votos_audios: 0,
          perfiles_completos: 0,
          meta: []
        };
        totalAlumnosPorTaller.value[taller.id] = 0;
        totalAnecdotasPorTaller.value[taller.id] = 0;
      } finally {
        isLoadingStats.value[taller.id] = false;
      }
    });

  } finally {
    isLoadingTalleres.value = false;
  }
};

// Cargar contenido de Builder
const loadContent = async () => {
try {
  const contentValue = await fetchOneEntry({
    model: 'page',
    apiKey: BUILDER_API_KEY,
    userAttributes: {
      urlPath: `/escuela-shopify`,
    },
    cacheSeconds: -1,
  });

  document.title = contentValue?.data?.title || 'Escuela';
  content.value = contentValue;
  canShowContent.value = contentValue ? true : isPreviewing();
  isContentReady.value = true;
} finally {
  isLoading.value = false;
}
};

// Cargar novedad de la comunidad Orsai
const cargarNovedad = async () => {
  try {
    isLoadingNovedad.value = true;
    const response = await axios.get('https://orsai.org/wp-json/wp/v2/posts?tags=14&_embed&per_page=1&orderby=date&order=desc');
    
    if (response.data && response.data.length > 0) {
      const post = response.data[0];
      const mediaDetails = post._embedded?.['wp:featuredmedia']?.[0]?.media_details;
      
      // Obtener la mejor imagen disponible
      let bestImage = null;
      if (mediaDetails?.sizes) {
        // Priorizar imágenes de mayor calidad
        bestImage = mediaDetails.sizes['1536x1536']?.source_url || 
                   mediaDetails.sizes.large?.source_url || 
                   mediaDetails.sizes.medium_large?.source_url || 
                   mediaDetails.sizes.medium?.source_url || 
                   post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
      } else {
        bestImage = post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
      }
      
      novedad.value = {
        id: post.id,
        title: post.title.rendered,
        excerpt: post.excerpt.rendered,
        link: post.link,
        date: post.date,
        author: post._embedded?.author?.[0]?.name || 'Orsai',
        featuredImage: bestImage,
        thumbnailImage: bestImage,
        linkPersonalizado: post.meta?.link_personalizado || null,
        footnotes: post.meta?.footnotes || ''
      };
    }
  } catch (error) {
    console.error('Error al cargar la novedad:', error);
    novedad.value = null;
  } finally {
    isLoadingNovedad.value = false;
  }
};

// Cargar datos del Excel CSV
const cargarDatosExcel = async () => {
  try {
    const csvUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTVIM6FnvXZfhM2Ugi2K4FXs7KKfITpYwLJW1sUkbmwou1jg6l4J9mpar3WfQjXel5sXCnFUEYg25To/pub?gid=0&single=true&output=csv';
    
    const response = await fetch(csvUrl);
    const csvText = await response.text();
    
    // Parsear el CSV con papaparse
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      complete: function(results) {
        console.log('📊 Datos del Excel cargados:', results.data);
        
        // Procesar los datos para convertir strings a números
        const datosProcesados = results.data.map(row => {
          return {
            ...row,
            'Vendidas': row['Vendidas'] ? parseInt(row['Vendidas']) || 0 : 0,
            'Media beca': row['Media beca'] ? parseInt(row['Media beca']) || 0 : 0,
            'Total': row['Total'] ? parseInt(row['Total']) || 0 : 0,
            'Días para cierre': row['Días para cierre'] ? parseInt(row['Días para cierre']) || 0 : 0
          };
        });
        
        console.log('📊 Datos procesados (strings convertidos a números):', datosProcesados);
        console.log('📊 Docentes encontrados en Excel:', datosProcesados.map(row => ({ docente: row['A la fecha:'], total: row['Total'] })).filter(row => row.docente && row.total > 0));
        
        // Almacenar los datos en la variable reactiva
        datosExcel.value = datosProcesados;
        
        // Encontrar el total de alumnos (primera fila con datos)
        const totalAlumnos = datosProcesados.find(row => row['Total'] && row['Total'] > 0);
        if (totalAlumnos) {
          console.log('Total de alumnos encontrado:', totalAlumnos['Total']);
        }
      },
      error: function(error) {
        console.error('Error al parsear el CSV:', error);
      }
    });
    
  } catch (error) {
    console.error('Error al cargar datos del Excel:', error);
  }
};

// Función para obtener el total de alumnos de un docente desde el Excel
// NOTA: Esta función ya NO se usa para mostrar el total de alumnos en la UI.
// Solo se mantiene para debug y referencia. El total de alumnos ahora viene de Strapi.
const obtenerTotalAlumnosDesdeExcel = (nombreDocente) => {
  if (!datosExcel.value || datosExcel.value.length === 0) {
    console.log(`No hay datos del Excel disponibles para buscar ${nombreDocente}`);
    return null;
  }
  
  console.log(`Buscando datos para el docente: "${nombreDocente}"`);
  console.log('Datos disponibles en Excel:', datosExcel.value.map(row => row['A la fecha:']));
  
  // Buscar el docente en los datos del Excel
  const docenteEncontrado = datosExcel.value.find(row => 
    row['A la fecha:'] && 
    row['A la fecha:'].toLowerCase().trim() === nombreDocente.toLowerCase().trim()
  );
  
  if (docenteEncontrado && docenteEncontrado['Total']) {
    console.log(`✅ Total de alumnos encontrado para ${nombreDocente}:`, docenteEncontrado['Total']);
    return docenteEncontrado['Total'];
  }
  
  console.log(`❌ No se encontró datos para el docente: ${nombreDocente}`);
  console.log('Intentando búsqueda parcial...');
  
  // Intentar búsqueda parcial por si el nombre no coincide exactamente
  const docenteParcial = datosExcel.value.find(row => 
    row['A la fecha:'] && 
    row['A la fecha:'].toLowerCase().includes(nombreDocente.toLowerCase()) ||
    nombreDocente.toLowerCase().includes(row['A la fecha:'].toLowerCase())
  );
  
  if (docenteParcial && docenteParcial['Total']) {
    console.log(`✅ Total de alumnos encontrado con búsqueda parcial para ${nombreDocente}:`, docenteParcial['Total']);
    return docenteParcial['Total'];
  }
  
  console.log(`❌ No se encontró datos ni con búsqueda parcial para: ${nombreDocente}`);
  return null;
};

// Función para validar si un número de alumnos es realista
const validarNumeroAlumnos = (numero, docente, fuente) => {
  // Números sospechosamente altos que podrían ser incorrectos
  const numerosSospechosos = [170, 200, 300, 500, 1000];
  
  if (numerosSospechosos.includes(numero)) {
    console.warn(`🚨 NÚMERO SOSPECHOSO: ${numero} alumnos para ${docente} desde ${fuente}. Este número podría ser incorrecto.`);
    return false;
  }
  
  if (numero > 150) {
    console.warn(`⚠️ ADVERTENCIA: Número de alumnos muy alto para ${docente}: ${numero} desde ${fuente}. Verificar datos.`);
    return false;
  }
  
  return true;
};

// Función para obtener el total de alumnos de un taller (usar solo datos de Strapi)
const obtenerTotalAlumnosTaller = (taller) => {
  console.log(`🔍 Obteniendo total de alumnos para taller: ${taller.titulo} (${taller.docente})`);
  console.log(`🔍 Slug del taller: ${taller.slug}`);
  
  // Usar siempre los datos del backend (Strapi)
  const totalStrapi = totalAlumnosPorTaller.value[taller.id] || 0;
  console.log(`📊 Usando datos de Strapi para ${taller.docente}: ${totalStrapi} alumnos`);
  
  // Validar si el número es realista
  validarNumeroAlumnos(totalStrapi, taller.docente, 'Strapi');
  
  return totalStrapi;
};

// Función para limpiar HTML y obtener texto plano
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Función para formatear la fecha
const formatearFechaNovedad = (dateString) => {
  if (!dateString) return '';
  const fecha = moment(dateString);
  return fecha.format('D [de] MMMM [de] YYYY');
};

onMounted(() => {
loadContent();
cargarTalleres();
cargarDatosExcel(); // Cargar datos del Excel
cargarNovedad(); // Cargar novedad para todos los usuarios
});

const getPercentageOfTotal = (value, total) => {
if (!value || !total) return 0;
const numValue = Number(value);
const numTotal = Number(total);
const percentage = Math.round((numValue / numTotal) * 100);
return Math.min(percentage, 100);
};

const formatNumber = (value) => {
if (value === undefined || value === null) return '0';
return Number(value).toLocaleString('es-AR');
};

// Función para calcular días restantes hasta la primera clase
const calcularDiasRestantes = (taller) => {
if (!taller?.agenda?.length) return null;

const primeraClase = new Date(taller.agenda[0].fecha);
const hoy = new Date();

// Si la fecha ya pasó, retornar null
if (primeraClase < hoy) return null;

const diasRestantes = moment(primeraClase).diff(moment(hoy), 'days');
return diasRestantes;
};

// Función para formatear la fecha del taller
const formatearFechaTaller = (taller) => {
if (!taller?.agenda?.length) return '';
const primeraClase = new Date(taller.agenda[0].fecha);
const mes = primeraClase.toLocaleDateString('es-ES', { month: 'long' }).toUpperCase();
const anio = primeraClase.getFullYear();
return `${mes} | ${anio}`;
};

const calcularProgresoTaller = (taller) => {
  // Si es taller-1 o taller-2, siempre está finalizado (100%)
  if (taller.slug === 'taller-1' || taller.slug === 'taller-2') {
    return 100;
  }

  // Si no hay agenda, retornar 0
  if (!taller?.agenda?.length) return 0;

  // Ordenar las clases por fecha
  const clasesOrdenadas = [...taller.agenda].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  const ahora = moment(); // Usar moment para mejor manejo de fechas
  const primeraClase = moment(clasesOrdenadas[0].fecha);
  const ultimaClase = moment(clasesOrdenadas[clasesOrdenadas.length - 1].fecha);

  // Si el taller aún no comenzó (primera clase es en el futuro)
  if (primeraClase.isAfter(ahora)) {
    console.log(`Taller ${taller.titulo} aún no comenzó. Primera clase: ${primeraClase.format('YYYY-MM-DD HH:mm')}`);
    return 0;
  }

  // Calcular clases realizadas, considerando la hora real de la clase
  const clasesRealizadas = clasesOrdenadas.filter(clase => {
    const fechaClase = moment(clase.fecha);
    // Si la clase es de un día anterior, ya está realizada
    if (fechaClase.isBefore(ahora, 'day')) return true;
    
    // Si la clase es hoy, verificar si ya pasó la hora de inicio real de la clase
    if (fechaClase.isSame(ahora, 'day')) {
      // Usar la hora real de la clase desde los datos de la agenda
      return ahora.isSameOrAfter(fechaClase);
    }
    
    return false;
  }).length;

  const totalClases = clasesOrdenadas.length;
  const progreso = Math.round((clasesRealizadas / totalClases) * 100);

  // Si el taller ya finalizó, asegurarnos de que muestre 100%
  if (ultimaClase.isBefore(ahora)) {
    console.log(`Taller ${taller.titulo} finalizado. Progreso: 100%`);
    return 100;
  }

  console.log(`Taller ${taller.titulo} en curso:`, {
    primeraClase: primeraClase.format('YYYY-MM-DD HH:mm'),
    ultimaClase: ultimaClase.format('YYYY-MM-DD HH:mm'),
    ahora: ahora.format('YYYY-MM-DD HH:mm'),
    clasesRealizadas,
    totalClases,
    progreso: `${progreso}%`,
    horaActual: ahora.format('HH:mm')
  });

  return progreso;
};

// Función para determinar si mostrar el certificado
const mostrarBotonCertificado = (taller) => {
  // Si es taller-1 o taller-2, siempre mostrar certificado
  if (taller.slug === 'taller-1' || taller.slug === 'taller-2') return true;
  
  // Para otros talleres, verificar si ya finalizó
  if (!taller?.agenda?.length) return false;
  const ultimaClase = new Date([...taller.agenda].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0].fecha);
  return ultimaClase < new Date();
};

// Función para formatear la fecha de la próxima o última clase en formato mobile
const formatNextClassDateMobile = (taller) => {
if (!taller?.agenda?.length) {
  if (taller.progreso >= 100) return { prefix: 'Finalizado', date: '' };
  return { prefix: 'Fecha no disponible', date: '' };
}

const now = moment();

if (taller.progreso >= 100) {
  // Find the last class
  const lastClass = taller.agenda
    .map(clase => ({ ...clase, date: moment(clase.fecha) }))
    .sort((a, b) => b.date.valueOf() - a.date.valueOf()) // Sort descending to get the last one
    [0];

  if (!lastClass) return { prefix: 'Finalizado', date: '' };

  // Format the last class date part: DOM 8 JUNIO
  const formattedDate = `${lastClass.date.format('ddd').toUpperCase()} ${lastClass.date.format('D')} ${lastClass.date.format('MMMM').toUpperCase()}`;

  return { prefix: 'Finalizó el', date: formattedDate };

} else {
  // Find the next upcoming class
  const nextClass = taller.agenda
    .map(clase => ({ ...clase, date: moment(clase.fecha) }))
    .filter(clase => clase.date.isSameOrAfter(now, 'day'))
    .sort((a, b) => a.date.valueOf() - b.date.valueOf())
    [0];

  if (!nextClass) return { prefix: 'No hay próximas clases', date: '' };

  // Format the date part: DOM 8 JUNIO
  const formattedDate = `${nextClass.date.format('ddd').toUpperCase()} ${nextClass.date.format('D')} ${nextClass.date.format('MMMM').toUpperCase()}`;

  return { prefix: 'PRÓX. CLASE EL', date: formattedDate };
}
};

// Computed properties para separar talleres activos y finalizados
const talleresActivos = computed(() => {
  // Si es docente, mostramos sus talleres como alumno
  return talleres.value?.filter(taller => {
    // Un taller está activo si:
    // 1. No es taller-1 ni taller-2 (que siempre van a finalizados)
    // 2. No es taller-septiembre (excluido de la lista)
    // 3. Tiene fecha de primera clase y aún no ha comenzado (próximamente)
    // 4. Ya comenzó pero aún no finalizó (en curso)
    if (taller.slug === 'taller-1' || taller.slug === 'taller-2' || taller.slug === 'taller-septiembre') return false;
    
    // Si no tiene agenda, no está activo
    if (!taller?.agenda?.length) return false;

    const ahora = new Date();
    const primeraClase = new Date(taller.agenda[0].fecha);
    const ultimaClase = new Date(taller.agenda[taller.agenda.length - 1].fecha);

    // Está activo si:
    // - Ya comenzó (primera clase es en el pasado) Y no ha finalizado (última clase es en el futuro)
    // - O si aún no comenzó (primera clase es en el futuro)
    return (primeraClase <= ahora && ultimaClase > ahora) || primeraClase > ahora;
  }).sort((a, b) => {
    const ahora = new Date();
    const aPrimeraClase = a.agenda?.length ? new Date(a.agenda[0].fecha) : null;
    const bPrimeraClase = b.agenda?.length ? new Date(b.agenda[0].fecha) : null;

    // Si ambos talleres tienen fecha de primera clase
    if (aPrimeraClase && bPrimeraClase) {
      // Determinar si cada taller ya comenzó
      const aYaComenzo = aPrimeraClase <= ahora;
      const bYaComenzo = bPrimeraClase <= ahora;

      // Si uno ya comenzó y el otro no, el que ya comenzó va primero
      if (aYaComenzo && !bYaComenzo) return -1;
      if (!aYaComenzo && bYaComenzo) return 1;

      // Si ambos ya comenzaron, ordenar por fecha de primera clase descendente (más reciente primero)
      if (aYaComenzo && bYaComenzo) {
        return bPrimeraClase - aPrimeraClase;
      }

      // Si ambos son futuros, ordenar por fecha ascendente (más próximo primero)
      return aPrimeraClase - bPrimeraClase;
    }

    // Si solo uno tiene fecha, poner el que tiene fecha primero
    if (aPrimeraClase) return -1;
    if (bPrimeraClase) return 1;

    // Si ninguno tiene fecha, mantener el orden por ID
    return a.id - b.id;
  }) || [];
});

const talleresFinalizados = computed(() => {
  // Si es docente, mostramos sus talleres finalizados como alumno
  return talleres.value?.filter(taller => {
    // Un taller está finalizado si:
    // 1. Es taller-1 o taller-2
    // 2. Ya pasó su última clase
    if (taller.slug === 'taller-1' || taller.slug === 'taller-2') return true;
    
    // Si no tiene agenda, no está finalizado
    if (!taller?.agenda?.length) return false;

    const ahora = new Date();
    const ultimaClase = new Date(taller.agenda[taller.agenda.length - 1].fecha);

    // Está finalizado si la última clase ya pasó
    return ultimaClase < ahora;
  }).sort((a, b) => {
    // El taller-1 siempre va al final (es el más viejo)
    if (a.slug === 'taller-1') return 1;
    if (b.slug === 'taller-1') return -1;
    
    // El taller-2 va antes que el taller-1
    if (a.slug === 'taller-2') return 1;
    if (b.slug === 'taller-2') return -1;

    // Para el resto de talleres, ordenar por fecha de primera clase, del más nuevo al más viejo
    if (a.fechaPrimeraClase && b.fechaPrimeraClase) {
      return b.fechaPrimeraClase - a.fechaPrimeraClase;
    }

    // Si solo uno tiene fecha, poner el que tiene fecha primero
    if (a.fechaPrimeraClase) return -1;
    if (b.fechaPrimeraClase) return 1;

    // Si ninguno tiene fecha, mantener el orden por ID
    return a.id - b.id;
  }) || [];
});

// Nuevas computed properties para separar talleres del docente y como alumno
const talleresDelDocente = computed(() => {
  return talleresDocente.value || [];
});

// Talleres del docente: activos (en curso o por empezar) ordenados de más próximo a más lejano a empezar
const talleresDocenteActivos = computed(() => {
  const list = talleresDocente.value || [];
  return list.filter(taller => {
    if (taller.slug === 'taller-1' || taller.slug === 'taller-2') return false;
    if (!taller?.agenda?.length) return true; // sin agenda lo mostramos en activos
    const ultimaClase = new Date([...taller.agenda].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0].fecha);
    return ultimaClase >= new Date(); // no finalizado
  }).sort((a, b) => {
    const aFecha = a.fechaPrimeraClase ? new Date(a.fechaPrimeraClase).getTime() : Infinity;
    const bFecha = b.fechaPrimeraClase ? new Date(b.fechaPrimeraClase).getTime() : Infinity;
    return aFecha - bFecha; // más próximo a empezar primero
  });
});

// Talleres del docente: finalizados (debajo de los activos)
const talleresDocenteFinalizados = computed(() => {
  const list = talleresDocente.value || [];
  return list.filter(taller => {
    if (taller.slug === 'taller-1' || taller.slug === 'taller-2') return true;
    if (!taller?.agenda?.length) return false;
    const ultimaClase = new Date([...taller.agenda].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0].fecha);
    return ultimaClase < new Date();
  }).sort((a, b) => {
    if (a.slug === 'taller-1') return 1;
    if (b.slug === 'taller-1') return -1;
    if (a.slug === 'taller-2') return 1;
    if (b.slug === 'taller-2') return -1;
    const aFecha = a.fechaPrimeraClase ? new Date(a.fechaPrimeraClase).getTime() : 0;
    const bFecha = b.fechaPrimeraClase ? new Date(b.fechaPrimeraClase).getTime() : 0;
    return bFecha - aFecha; // más reciente primero
  });
});

// Talleres del docente ordenados: primero activos (más próximo a empezar), luego finalizados
const talleresDocenteOrdenados = computed(() => {
  return [...(talleresDocenteActivos.value || []), ...(talleresDocenteFinalizados.value || [])];
});

// Índice del primer taller del docente finalizado dentro de la lista ordenada
const indicePrimerTallerDocenteFinalizado = computed(() => {
  if (!talleresDocenteFinalizados.value?.length) return -1;
  const idsFinalizados = new Set(talleresDocenteFinalizados.value.map(t => t.id));
  return talleresDocenteOrdenados.value.findIndex(t => idsFinalizados.has(t.id));
});

const talleresComoAlumno = computed(() => {
  // Filtrar solo los talleres donde el usuario está inscrito como alumno
  // Estos son los talleres que NO están en talleresDocente
  const idsTalleresDocente = talleresDocente.value?.map(t => t.id) || [];
  return talleres.value?.filter(taller => !idsTalleresDocente.includes(taller.id)) || [];
});

const talleresActivosComoAlumno = computed(() => {
  return talleresComoAlumno.value?.filter(taller => {
    // Un taller está activo si:
    // 1. No es taller-1 ni taller-2 (que siempre van a finalizados)
    // 2. No es taller-septiembre (excluido de la lista)
    // 3. Tiene fecha de primera clase y aún no ha comenzado (próximamente)
    // 4. Ya comenzó pero aún no finalizó (en curso)
    if (taller.slug === 'taller-1' || taller.slug === 'taller-2' || taller.slug === 'taller-septiembre') return false;
    
    // Si no tiene agenda, no está activo
    if (!taller?.agenda?.length) return false;

    const ahora = new Date();
    const primeraClase = new Date(taller.agenda[0].fecha);
    const ultimaClase = new Date(taller.agenda[taller.agenda.length - 1].fecha);

    // Está activo si:
    // - Ya comenzó (primera clase es en el pasado) Y no ha finalizado (última clase es en el futuro)
    // - O si aún no comenzó (primera clase es en el futuro)
    return (primeraClase <= ahora && ultimaClase > ahora) || primeraClase > ahora;
  }).sort((a, b) => {
    const ahora = new Date();
    const aPrimeraClase = a.agenda?.length ? new Date(a.agenda[0].fecha) : null;
    const bPrimeraClase = b.agenda?.length ? new Date(b.agenda[0].fecha) : null;

    // Si ambos talleres tienen fecha de primera clase
    if (aPrimeraClase && bPrimeraClase) {
      // Determinar si cada taller ya comenzó
      const aYaComenzo = aPrimeraClase <= ahora;
      const bYaComenzo = bPrimeraClase <= ahora;

      // Si uno ya comenzó y el otro no, el que ya comenzó va primero
      if (aYaComenzo && !bYaComenzo) return -1;
      if (!aYaComenzo && bYaComenzo) return 1;

      // Si ambos ya comenzaron, ordenar por fecha de primera clase descendente (más reciente primero)
      if (aYaComenzo && bYaComenzo) {
        return bPrimeraClase - aPrimeraClase;
      }

      // Si ambos son futuros, ordenar por fecha ascendente (más próximo primero)
      return aPrimeraClase - bPrimeraClase;
    }

    // Si solo uno tiene fecha, poner el que tiene fecha primero
    if (aPrimeraClase) return -1;
    if (bPrimeraClase) return 1;

    // Si ninguno tiene fecha, mantener el orden por ID
    return a.id - b.id;
  }) || [];
});

const talleresFinalizadosComoAlumno = computed(() => {
  return talleresComoAlumno.value?.filter(taller => {
    // Un taller está finalizado si:
    // 1. Es taller-1 o taller-2
    // 2. Ya pasó su última clase
    if (taller.slug === 'taller-1' || taller.slug === 'taller-2') return true;
    
    // Si no tiene agenda, no está finalizado
    if (!taller?.agenda?.length) return false;

    const ahora = new Date();
    const ultimaClase = new Date(taller.agenda[taller.agenda.length - 1].fecha);

    // Está finalizado si la última clase ya pasó
    return ultimaClase < ahora;
  }).sort((a, b) => {
    // El taller-1 siempre va al final (es el más viejo)
    if (a.slug === 'taller-1') return 1;
    if (b.slug === 'taller-1') return -1;
    
    // El taller-2 va antes que el taller-1
    if (a.slug === 'taller-2') return 1;
    if (b.slug === 'taller-2') return -1;

    // Para el resto de talleres, ordenar por fecha de primera clase, del más nuevo al más viejo
    if (a.fechaPrimeraClase && b.fechaPrimeraClase) {
      return b.fechaPrimeraClase - a.fechaPrimeraClase;
    }

    // Si solo uno tiene fecha, poner el que tiene fecha primero
    if (a.fechaPrimeraClase) return -1;
    if (b.fechaPrimeraClase) return 1;

    // Si ninguno tiene fecha, mantener el orden por ID
    return a.id - b.id;
  }) || [];
});

// Función para generar la URL del certificado
const getCertificadoUrl = async (taller) => {
try {
  if (!taller?.id || !usuario.value?.email) {
    console.warn('No se pudo generar la URL del certificado:', {
      tallerId: taller?.id,
      email: usuario.value?.email
    });
    return null;
  }

  // Obtener el alumno específico para este taller
  const response = await strapiService.getAlumnoByUsuarioAndTaller(usuario.value.email, taller.slug);
  const alumno = response.data?.[0];

  if (!alumno?.id) {
    console.warn('No se encontró el alumno para este taller');
    return null;
  }

  return `https://backend.orsai.org/apis/talleres/v1/${taller.id}/certificado/${alumno.id}`;
} catch (error) {
  console.error('Error al obtener el alumno:', error);
  return null;
}
};

// Función para manejar el click del botón
const handleDescargarCertificado = async (taller) => {
const url = await getCertificadoUrl(taller);
if (url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}
};

// Computed para la ruta del taller
const getTallerRoute = (taller, esComoAlumno = false) => {
  // Si es docente y no estamos viendo el taller como alumno, usar ruta de docentes
  if (usuario.value?.docente && !esComoAlumno) {
    return `/docentes/${taller.slug}`;
  }
  // Para todos los demás casos (alumnos o docentes viendo como alumno), usar ruta de virtuales
  return `/virtuales/${taller.docente?.toLowerCase().replace(' ', '-')}/${taller.slug}/inicio`;
};

// Función para determinar el estado del taller
const getEstadoTaller = (taller) => {
  // Si es taller-1 o taller-2, siempre está finalizado
  if (taller.slug === 'taller-1' || taller.slug === 'taller-2') {
    return 'Finalizado';
  }
  
  if (!taller?.agenda?.length) return 'Próximamente';
  
  const ahora = new Date();
  const primeraClase = new Date(taller.agenda[0].fecha);
  const ultimaClase = new Date(taller.agenda[taller.agenda.length - 1].fecha);

  if (primeraClase > ahora) {
    // Formatear la fecha de inicio
    const fechaInicio = moment(primeraClase);
    return `Inicia el ${fechaInicio.format('D [de] MMMM')}`;
  }
  if (ultimaClase < ahora) return 'Finalizado';
  return 'En curso';
};

// Función para truncar el título del taller
const truncarTituloTaller = (titulo, maxLength = 50) => {
  if (!titulo) return '';
  if (titulo.length <= maxLength) return titulo;
  return titulo.substring(0, maxLength) + '...';
};
</script>

<template>
<div>
  <HeaderTalleres />
  <div class="max-w-6xl mx-auto mb-10 lg:mb-16 md:p-5 pt-0 px-4 relative">
    <!-- Sección de Mis talleres solo visible para usuarios logueados -->
    <template v-if="esUsuario">
      <!-- Loading state para novedad -->
      <div v-if="isLoadingNovedad" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>

      <!-- Novedad de la comunidad -->
      <div v-else-if="novedad" class="mb-8 mt-8">
        <div class="bg-white md:mb-16 rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
          <!-- Desktop Layout -->
          <div class="hidden md:flex h-auto">
            <!-- Imagen -->
            <div class="w-1/2 flex-shrink-0">
              <div class="relative w-full h-full">
                <img 
                  :src="novedad.featuredImage" 
                  :alt="novedad.title"
                  class="absolute inset-0 w-full h-full object-cover"
                  @error="$event.target.style.display='none'"
                />
                <div v-if="!novedad.featuredImage" class="absolute inset-0 bg-gray-100 flex items-center justify-center">
                  <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Contenido -->
            <div class="flex-1 p-6 flex flex-col justify-between">
              <div>
                <div class="flex items-center gap-2 mb-3">
                  <span class="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                    Novedad
                  </span>
                  <span class="text-sm text-gray-500">
                    {{ formatearFechaNovedad(novedad.date) }}
                  </span>
                </div>
                
                <h3 class="font-inter font-bold text-[16px] leading-[128%] tracking-[0%] md:text-[24px] md:leading-[120%] text-gray-900 mb-3 leading-tight hover:text-blue-600 transition-colors">
                  {{ novedad.title }}
                </h3>
                
                <p class="text-gray-700 leading-relaxed line-clamp-3">
                  {{ stripHtml(novedad.excerpt) }}
                </p>

              </div>
              
              <div class="mt-6">
                <a :href="novedad.linkPersonalizado || novedad.link" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   class="inline-flex items-center gap-2 bg-gray-900 text-sm text-white px-3 py-2 rounded-lg font-semibold hover:bg-gray-800 transition-colors">
                  Leer más
                  <ChevronRightIcon class="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <!-- Mobile Layout -->
          <div class="md:hidden">
            <!-- Imagen -->
            <div class="w-full">
              <div class="relative w-full h-56">
                <img 
                  :src="novedad.featuredImage" 
                  :alt="novedad.title"
                  class="absolute inset-0 w-full h-full object-cover"
                  @error="$event.target.style.display='none'"
                />
                <div v-if="!novedad.featuredImage" class="absolute inset-0 bg-gray-100 flex items-center justify-center">
                  <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Contenido -->
            <div class="p-4">
              <div class="flex items-center gap-2 mb-3">
                <span class="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Novedad
                </span>
                <span class="text-xs text-gray-500">
                  {{ formatearFechaNovedad(novedad.date) }}
                </span>
              </div>
              
              <h3 class="font-inter font-bold text-[16px] leading-[128%] tracking-[0%] md:text-[24px] md:leading-[120%] text-gray-900 mb-3 leading-tight hover:text-blue-600 transition-colors">
                {{ novedad.title }}
                </h3>
              
              <p class="text-gray-700 text-sm leading-relaxed mb-3 line-clamp-2">
                {{ stripHtml(novedad.excerpt) }}
              </p>
              
              <div class="flex items-center justify-end">
                <a :href="novedad.linkPersonalizado || novedad.link" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   class="inline-flex items-center gap-1 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors">
                  Leer más
                  <ChevronRightIcon class="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Titulo principal -->
      <h2 v-if="!esDocente" class="text-4xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-6 px-2 mt-4 md:px-0 leading-tight font-inter text-[18px] md:text-4xl leading-[128%] tracking-[0%]">Zona personal: Tus talleres</h2>
      <h2 v-else class="text-4xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-6 px-2 mt-4 md:px-0 leading-tight font-inter text-[18px] md:text-4xl leading-[128%] tracking-[0%]">Zona personal</h2>

      <!-- Talleres del docente (ordenados: más próximo a empezar primero, finalizados abajo) -->
      <template v-if="esDocente && talleresDocenteOrdenados.length > 0">
        <h3 class="text-2xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 px-2 md:px-0 mt-2 md:mt-6 leading-tight font-inter text-[16px] md:text-2xl leading-[128%] tracking-[0%]">Tus talleres como docente</h3>
        <div class="flex flex-col gap-6 mt-6">
          <template v-for="(taller, index) in talleresDocenteOrdenados" :key="taller.id">
            <!-- Separador y título para los talleres finalizados del docente -->
            <div
              v-if="index === indicePrimerTallerDocenteFinalizado && indicePrimerTallerDocenteFinalizado !== -1"
              class="mt-10 mb-2 px-2 md:px-0"
            >
              <h3 class="text-2xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-4 leading-tight font-inter text-[16px] md:text-2xl leading-[128%] tracking-[0%]">
                Tus talleres como docente finalizados
              </h3>
            </div>

            <div
              class="bg-white rounded-2xl shadow-md border border-gray-200 items-stretch overflow-hidden"
            >
            <!-- Desktop and Tablet Layout -->
            <div class="hidden md:flex flex-row items-stretch flex-wrap">
              <!-- Imagen del taller -->
              <div class="w-[22%] flex-shrink-0">
                <router-link :to="getTallerRoute(taller)" class="block w-full h-full">
                  <div class="relative w-full aspect-square">
                    <img :src="taller.imagen" :alt="taller.titulo" class="absolute inset-0 w-full h-full object-cover" />
                  </div>
                </router-link>
              </div>
              <!-- Contenido del taller -->
              <div class="flex flex-col justify-between md:flex-row p-4 gap-4 flex-1">
                <!-- Info principal del taller -->
                <div class="min-w-0 flex flex-col justify-center w-full md:w-[200px]">
                  <div class="text-xs text-gray-600 font-semibold uppercase mb-1 tracking-wide">
                    {{ formatearFechaTaller(taller) }}</div>
                  <h3 class="text-2xl font-extrabold text-black mb-4 leading-tight break-words w-full">
                    {{ truncarTituloTaller(taller.titulo) }}
                  </h3>
                  <div class="mt-auto">
                    <span class="text-base font-bold text-black">Docente</span><br>
                    <span class="text-base text-gray-800">{{ taller.docente }}</span>
                  </div>
                </div>
                <!-- Métricas y progreso -->
                <div class="flex flex-col md:flex-row flex-wrap gap-4 flex-1 w-full">
                  <!-- Card Alumnos para docentes -->
                  <div class="hidden md:flex bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full md:flex-1 min-w-[180px]">
                    <div class="flex items-center gap-2 mb-2">
                      <span class="bg-blue-100 text-blue-600 rounded-xl p-2 flex items-center justify-center">
                        <img src="@/assets/images/Talleres/alumnos.png" alt="Alumnos" class="w-6 h-6 object-contain" />
                      </span>
                      <div>
                        <div class="text-sm font-bold text-gray-800 leading-tight">Alumnos</div>
                        <div class="text-xs text-gray-500">Participación y actividad</div>
                      </div>
                    </div>
                    <div class="text-2xl font-extrabold text-gray-900 mb-1">
                      {{ formatNumber(obtenerTotalAlumnosTaller(taller)) }}
                    </div>
                    <div class="text-sm text-gray-500 mb-2">Total de alumnos</div>
                    <div class="mt-auto">
                      <div class="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Total de alumnos</span>
                        <span class="font-bold text-black">
                          {{ formatNumber(obtenerTotalAlumnosTaller(taller)) }}
                        </span>
                      </div>
                      <div class="w-full h-2 bg-gray-200 rounded-full">
                        <div class="h-2 bg-blue-500 rounded-full" style="width: 100%"></div>
                      </div>
                    </div>
                  </div>
                  <!-- Card Anécdotas -->
                  <div class="hidden md:flex bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full md:flex-1 min-w-[180px]">
                    <div class="flex items-center gap-2 mb-2">
                      <span class="bg-purple-100 text-purple-600 rounded-xl p-2 flex items-center justify-center">
                        <img src="@/assets/images/Talleres/anecdotas.png" alt="Tareas" class="w-6 h-6 object-contain" />
                      </span>
                      <div>
                        <div class="text-sm font-bold text-gray-800 leading-tight">{{ taller.terminoPlural ?
                          taller.terminoPlural.charAt(0).toUpperCase() + taller.terminoPlural.slice(1) : 'Tareas' }}</div>
                        <div class="text-xs text-gray-500">Contexto y valoraciones</div>
                      </div>
                    </div>
                    <div class="text-2xl font-extrabold text-gray-900 mb-1">
                      {{ formatNumber(totalAnecdotasPorTaller[taller.id] || 0) }}
                    </div>
                    <div class="mt-auto">
                      <div class="flex flex-col gap-2">
                        <template v-if="taller.docente?.toLowerCase().includes('hernan casciari')">
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Con audio</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(estadisticasPorTaller[taller.id]?.cuentos_con_audio || 0) }}
                                <span class="text-gray-400">({{
                                  getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_audio,
                                    totalAnecdotasPorTaller[taller.id]) }}%)</span>
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-blue-500 rounded-full"
                                :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_audio, totalAnecdotasPorTaller[taller.id])}%` }">
                              </div>
                            </div>
                          </div>
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Con resúmenes</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes || 0) }}
                                <span class="text-gray-400">({{
                                  getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes,
                                    totalAnecdotasPorTaller[taller.id]) }}%)</span>
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-purple-500 rounded-full"
                                :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes, totalAnecdotasPorTaller[taller.id])}%` }">
                              </div>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Total de {{ taller.terminoPlural?.toLowerCase() || 'tareas' }}</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(totalAnecdotasPorTaller[taller.id] || 0) }}
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-purple-500 rounded-full"
                                :style="{ width: totalAnecdotasPorTaller[taller.id] > 0 ? '100%' : '0%' }">
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                  <!-- Card de progreso -->
                  <div class="flex flex-col justify-between w-full md:flex-1 items-end">
                    <div class="w-full">
                      <div class="text-sm font-bold text-black mb-1">Progreso</div>
                      <div class="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                        <div class="h-full rounded-full transition-all" 
                             :class="{
                               'bg-[rgba(49,159,67,1)]': taller.progreso === 100 || taller.slug === 'taller-1' || taller.slug === 'taller-2',
                               'bg-green-500': taller.progreso > 0 && taller.progreso < 100,
                               'bg-gray-400': taller.progreso === 0
                             }"
                             :style="{ width: `${taller.progreso}%` }">
                        </div>
                      </div>
                      <div class="text-right mb-4">
                        <span class="text-sm font-bold text-black">
                          {{ getEstadoTaller(taller) }}
                        </span>
                        <div v-if="taller.progreso > 0 && taller.progreso < 100" class="text-xs text-gray-500 mt-0.5">
                          {{ Math.round(taller.progreso) }}% completado
                        </div>
                      </div>
                    </div>
                    <template v-if="mostrarBotonCertificado(taller) && !usuario.value?.docente">
                      <button @click="handleDescargarCertificado(taller)"
                        class="w-full py-2 text-black rounded-lg text-base font-bold flex items-center justify-end transition text-right hover:opacity-80">
                        <span class="flex items-center">
                          Certificado
                          <ArrowDownTrayIcon class="ml-2 w-4 h-4" />
                        </span>
                      </button>
                    </template>
                    <template v-else>
                      <router-link :to="getTallerRoute(taller)"
                        class="w-full py-2 text-black rounded-lg text-base font-bold flex items-center justify-end transition text-right hover:opacity-80">
                        <span class="flex items-center">
                          Ir al taller
                          <ChevronRightIcon class="ml-2 w-4 h-4" />
                        </span>
                      </router-link>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile Layout -->
            <div class="flex md:hidden flex-row items-stretch p-4 gap-12">
              <!-- Info principal del taller -->
              <div class="min-w-0 flex flex-col flex-1 justify-evenly">
                <div class="text-black mb-1 tracking-wide text-[10px] leading-[130%] uppercase">
                  <span class="font-inter font-normal">{{ formatNextClassDateMobile(taller).prefix }}</span>
                  <span class="font-inter font-bold ml-1">{{ formatNextClassDateMobile(taller).date }}</span>
                </div>
                <h3 class="font-inter font-bold text-[16px] leading-[120%] tracking-[0%] text-black mb-2 break-words">
                  {{ truncarTituloTaller(taller.titulo) }}
                </h3>
                <div class="font-inter font-normal text-[10px] leading-[130%] tracking-[0%] uppercase text-gray-800">{{ taller.docente }}</div>

                <!-- Estado -->
                <div class="flex items-center">
                  <div class="w-1/4 h-3 rounded-full overflow-hidden flex-shrink-0 bg-gray-200">
                    <div class="h-full rounded-full transition-all"
                         :class="{
                           'bg-[rgba(49,159,67,1)]': taller.progreso === 100 || taller.slug === 'taller-1' || taller.slug === 'taller-2',
                           'bg-green-500': taller.progreso > 0 && taller.progreso < 100,
                           'bg-gray-400': taller.progreso === 0
                         }"
                         :style="{ width: `${taller.progreso}%` }">
                    </div>
                  </div>
                  <div class="ml-2 text-sm font-bold text-black">
                    <span class="font-inter font-bold text-[12px] leading-[132%] tracking-[0%] text-center align-middle">
                      {{ getEstadoTaller(taller) }}
                    </span>
                    <div v-if="taller.progreso > 0 && taller.progreso < 100" class="text-[10px] text-gray-500 mt-0.5">
                      {{ Math.round(taller.progreso) }}% completado
                    </div>
                  </div>
                </div>
              </div>

              <!-- Imagen del taller y Link -->
              <div class="flex flex-col items-end justify-between flex-1">
                <router-link :to="getTallerRoute(taller)" class="block w-24 flex-shrink-0 mb-2">
                  <div class="relative w-full aspect-square rounded-md overflow-hidden">
                    <img :src="taller.imagen" :alt="taller.titulo" class="absolute inset-0 w-full h-full object-cover" />
                  </div>
                </router-link>
                <template v-if="mostrarBotonCertificado(taller) && !usuario.value?.docente">
                  <button @click="handleDescargarCertificado(taller)"
                    class="font-inter font-bold text-xs leading-[140%] tracking-[0%] text-black flex items-center whitespace-nowrap hover:opacity-80">
                    <span class="flex items-center">
                      Certificado
                      <ArrowDownTrayIcon class="ml-1 w-3 h-3" />
                    </span>
                  </button>
                </template>
                <template v-else>
                  <router-link :to="getTallerRoute(taller)"
                    class="font-inter font-bold text-xs leading-[140%] tracking-[0%] text-black flex items-center whitespace-nowrap hover:opacity-80">
                    <span class="flex items-center">
                      Ir al taller
                      <ChevronRightIcon class="ml-1 w-3 h-3" />
                    </span>
                  </router-link>
                </template>
              </div>
            </div>
          </div>
          </template>
        </div>
      </template>


      <!-- Talleres activos - Visible para todos los usuarios, incluyendo docentes -->
      <template v-if="(esDocente && talleresActivosComoAlumno.length > 0) || (!esDocente && talleresActivos.length > 0)">
        <h3 v-if="esDocente" class="text-2xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 px-2 md:px-0 mt-12 leading-tight font-inter text-[16px] md:text-2xl leading-[128%] tracking-[0%]">Tus talleres como alumno</h3>
        <h3 v-else class="text-2xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 px-2 md:px-0 mt-2 md:mt-6 leading-tight font-inter text-[16px] md:text-2xl leading-[128%] tracking-[0%]">En progreso</h3>

        <!-- Loading state -->
        <div v-if="isLoadingTalleres" class="flex justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>

        <!-- Talleres activos grid -->
        <div v-else class="flex flex-col gap-6">
          <div v-for="taller in (esDocente ? talleresActivosComoAlumno : talleresActivos)" :key="taller.id"
            class="bg-white rounded-2xl shadow-md border border-gray-200 items-stretch overflow-hidden">
            <!-- Desktop and Tablet Layout -->
            <div class="hidden md:flex flex-row items-stretch flex-wrap">
              <!-- Imagen del taller -->
              <div class="w-[22%] flex-shrink-0">
                <router-link :to="getTallerRoute(taller, true)" class="block w-full h-full">
                  <div class="relative w-full aspect-square">
                    <img :src="taller.imagen" :alt="taller.titulo" class="absolute inset-0 w-full h-full object-cover" />
                  </div>
                </router-link>
              </div>
              <!-- Contenido del taller -->
              <div class="flex flex-col justify-between md:flex-row p-4 gap-4 flex-1">
                <!-- Info principal del taller -->
                <div class="min-w-0 flex flex-col justify-center w-full md:w-[200px]">
                  <div class="text-xs text-gray-600 font-semibold uppercase mb-1 tracking-wide">
                    {{ formatearFechaTaller(taller) }}</div>
                  <h3 class="text-2xl font-extrabold text-black mb-4 leading-tight break-words w-full">
                    {{ truncarTituloTaller(taller.titulo) }}
                  </h3>
                  <div class="mt-auto">
                    <span class="text-base font-bold text-black">Docente</span><br>
                    <span class="text-base text-gray-800">{{ taller.docente }}</span>
                  </div>
                </div>
                <!-- Métricas y progreso -->
                <div class="flex flex-col md:flex-row flex-wrap gap-4 flex-1 w-full">
                  <!-- Card Alumnos -->
                  <div class="hidden md:flex bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full md:flex-1 min-w-[180px]">
                    <div class="flex items-center gap-2 mb-2">
                      <span class="bg-blue-100 text-blue-600 rounded-xl p-2 flex items-center justify-center">
                        <img src="@/assets/images/Talleres/alumnos.png" alt="Alumnos" class="w-6 h-6 object-contain" />
                      </span>
                      <div>
                        <div class="text-sm font-bold text-gray-800 leading-tight">Alumnos</div>
                        <div class="text-xs text-gray-500">Participación y actividad</div>
                      </div>
                    </div>
                    <div class="text-2xl font-extrabold text-gray-900 mb-1">
                      {{ formatNumber(obtenerTotalAlumnosTaller(taller)) }}
                    </div>
                    <div class="text-sm text-gray-500 mb-2">Total de alumnos</div>
                    <div class="mt-auto">
                      <div class="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Perfiles completos</span>
                        <span class="font-bold text-black">
                          {{ formatNumber(estadisticasPorTaller[taller.id]?.perfiles_completos || 0) }}
                          <span class="text-gray-400">({{
                            getPercentageOfTotal(estadisticasPorTaller[taller.id]?.perfiles_completos,
                              obtenerTotalAlumnosTaller(taller)) }}%)</span>
                        </span>
                      </div>
                      <div class="w-full h-2 bg-gray-200 rounded-full">
                        <div class="h-2 bg-green-500 rounded-full"
                          :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.perfiles_completos, obtenerTotalAlumnosTaller(taller))}%` }">
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Card Anécdotas -->
                  <div class="hidden md:flex bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full md:flex-1 min-w-[180px]">
                    <div class="flex items-center gap-2 mb-2">
                      <span class="bg-purple-100 text-purple-600 rounded-xl p-2 flex items-center justify-center">
                        <img src="@/assets/images/Talleres/anecdotas.png" alt="Tareas" class="w-6 h-6 object-contain" />
                      </span>
                      <div>
                        <div class="text-sm font-bold text-gray-800 leading-tight">{{ taller.terminoPlural ?
                          taller.terminoPlural.charAt(0).toUpperCase() + taller.terminoPlural.slice(1) : 'Tareas' }}</div>
                        <div class="text-xs text-gray-500">Contexto y valoraciones</div>
                      </div>
                    </div>
                    <div class="text-2xl font-extrabold text-gray-900 mb-1">
                      {{ formatNumber(totalAnecdotasPorTaller[taller.id] || 0) }}
                    </div>
                    <div class="mt-auto">
                      <div class="flex flex-col gap-2">
                        <template v-if="taller.docente?.toLowerCase().includes('hernan casciari')">
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Con audio</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(estadisticasPorTaller[taller.id]?.cuentos_con_audio || 0) }}
                                <span class="text-gray-400">({{
                                  getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_audio,
                                    totalAnecdotasPorTaller[taller.id]) }}%)</span>
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-blue-500 rounded-full"
                                :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_audio, totalAnecdotasPorTaller[taller.id])}%` }">
                              </div>
                            </div>
                          </div>
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Con resúmenes</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes || 0) }}
                                <span class="text-gray-400">({{
                                  getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes,
                                    totalAnecdotasPorTaller[taller.id]) }}%)</span>
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-purple-500 rounded-full"
                                :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes, totalAnecdotasPorTaller[taller.id])}%` }">
                              </div>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Total de {{ taller.terminoPlural?.toLowerCase() || 'tareas' }}</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(totalAnecdotasPorTaller[taller.id] || 0) }}
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-purple-500 rounded-full"
                                :style="{ width: totalAnecdotasPorTaller[taller.id] > 0 ? '100%' : '0%' }">
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                  <!-- Card de progreso -->
                  <div class="flex flex-col justify-between w-full md:flex-1 items-end">
                    <div class="w-full">
                      <div class="text-sm font-bold text-black mb-1">Progreso</div>
                      <div class="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                        <div class="h-full rounded-full transition-all" 
                             :class="{
                               'bg-[rgba(49,159,67,1)]': taller.progreso === 100 || taller.slug === 'taller-1' || taller.slug === 'taller-2',
                               'bg-green-500': taller.progreso > 0 && taller.progreso < 100,
                               'bg-gray-400': taller.progreso === 0
                             }"
                             :style="{ width: `${taller.progreso}%` }">
                        </div>
                      </div>
                      <div class="text-right mb-4">
                        <span class="text-sm font-bold text-black">
                          {{ getEstadoTaller(taller) }}
                        </span>
                        <div v-if="taller.progreso > 0 && taller.progreso < 100" class="text-xs text-gray-500 mt-0.5">
                          {{ Math.round(taller.progreso) }}% completado
                        </div>
                      </div>
                    </div>
                    <template v-if="mostrarBotonCertificado(taller) && !usuario.value?.docente">
                      <button @click="handleDescargarCertificado(taller)"
                        class="w-full py-2 text-black rounded-lg text-base font-bold flex items-center justify-end transition text-right hover:opacity-80">
                        <span class="flex items-center">
                          Certificado
                          <ArrowDownTrayIcon class="ml-2 w-4 h-4" />
                        </span>
                      </button>
                    </template>
                    <template v-else>
                      <router-link :to="getTallerRoute(taller, true)"
                        class="w-full py-2 text-black rounded-lg text-base font-bold flex items-center justify-end transition text-right hover:opacity-80">
                        <span class="flex items-center">
                          Ir al taller
                          <ChevronRightIcon class="ml-2 w-4 h-4" />
                        </span>
                      </router-link>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile Layout -->
            <div class="flex md:hidden flex-row items-stretch p-4 gap-12">
              <!-- Info principal del taller -->
              <div class="min-w-0 flex flex-col flex-1 justify-evenly">
                <div class="text-black mb-1 tracking-wide text-[10px] leading-[130%] uppercase">
                  <span class="font-inter font-normal">{{ formatNextClassDateMobile(taller).prefix }}</span>
                  <span class="font-inter font-bold ml-1">{{ formatNextClassDateMobile(taller).date }}</span>
                </div>
                <h3 class="font-inter font-bold text-[16px] leading-[120%] tracking-[0%] text-black mb-2 break-words">
                  {{ truncarTituloTaller(taller.titulo) }}
                </h3>
                <div class="font-inter font-normal text-[10px] leading-[130%] tracking-[0%] uppercase text-gray-800">{{ taller.docente }}</div>

                <!-- Estado -->
                <div class="flex items-center">
                  <div class="w-1/4 h-3 rounded-full overflow-hidden flex-shrink-0 bg-gray-200">
                    <div class="h-full rounded-full transition-all"
                         :class="{
                           'bg-[rgba(49,159,67,1)]': taller.progreso === 100 || taller.slug === 'taller-1' || taller.slug === 'taller-2',
                           'bg-green-500': taller.progreso > 0 && taller.progreso < 100,
                           'bg-gray-400': taller.progreso === 0
                         }"
                         :style="{ width: `${taller.progreso}%` }">
                    </div>
                  </div>
                  <div class="ml-2 text-sm font-bold text-black">
                    <span class="font-inter font-bold text-[12px] leading-[132%] tracking-[0%] text-center align-middle">
                      {{ getEstadoTaller(taller) }}
                    </span>
                    <div v-if="taller.progreso > 0 && taller.progreso < 100" class="text-[10px] text-gray-500 mt-0.5">
                      {{ Math.round(taller.progreso) }}% completado
                    </div>
                  </div>
                </div>
              </div>

              <!-- Imagen del taller y Link -->
              <div class="flex flex-col items-end justify-between flex-1">
                 <router-link :to="getTallerRoute(taller, true)" class="block w-24 flex-shrink-0 mb-2">
                   <div class="relative w-full aspect-square rounded-md overflow-hidden">
                     <img :src="taller.imagen" :alt="taller.titulo" class="absolute inset-0 w-full h-full object-cover" />
                   </div>
                 </router-link>
                 <template v-if="mostrarBotonCertificado(taller) && !usuario.value?.docente">
                   <button @click="handleDescargarCertificado(taller)"
                     class="font-inter font-bold text-xs leading-[140%] tracking-[0%] text-black flex items-center whitespace-nowrap hover:opacity-80">
                     <span class="flex items-center">
                       Certificado
                       <ArrowDownTrayIcon class="ml-1 w-3 h-3" />
                     </span>
                   </button>
                 </template>
                 <template v-else>
                   <router-link :to="getTallerRoute(taller, true)"
                     class="font-inter font-bold text-xs leading-[140%] tracking-[0%] text-black flex items-center whitespace-nowrap hover:opacity-80">
                     <span class="flex items-center">
                       Ir al taller
                       <ChevronRightIcon class="ml-1 w-3 h-3" />
                     </span>
                   </router-link>
                 </template>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Talleres finalizados - Visible para todos los usuarios, incluyendo docentes -->
      <template v-if="(esDocente && talleresFinalizadosComoAlumno.length > 0) || (!esDocente && talleresFinalizados.length > 0)">
        <!-- Títulos para docentes -->
        <template v-if="esDocente">
          <h3 class="text-2xl font-bold text-gray-900 mb-4 md:mb-6 px-2 md:px-0 mt-12 leading-tight hidden md:block">Talleres finalizados como alumno</h3>
          <h3 class="font-inter font-bold text-[16px] leading-[128%] tracking-[0%] text-gray-900 mb-4 px-2 mt-12 md:hidden">Talleres finalizados como alumno</h3>
        </template>
        <!-- Títulos para alumnos -->
        <template v-else>
          <h3 class="text-2xl font-bold text-gray-900 mb-4 md:mb-6 px-2 md:px-0 mt-12 leading-tight hidden md:block">Finalizados</h3>
          <h3 class="font-inter font-bold text-[16px] leading-[128%] tracking-[0%] text-gray-900 mb-4 px-2 mt-12 md:hidden">Finalizados</h3>
        </template>

        <!-- Loading state -->
        <div v-if="isLoadingTalleres" class="flex justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>

        <div class="flex flex-col gap-6">
          <div v-for="taller in (esDocente ? talleresFinalizadosComoAlumno : talleresFinalizados)" :key="taller.id"
            class="bg-white rounded-2xl shadow-md border border-gray-200 items-stretch overflow-hidden">
             <!-- Desktop and Tablet Layout -->
            <div class="hidden md:flex flex-row items-stretch flex-wrap">
              <!-- Imagen del taller -->
              <div class="w-[22%] flex-shrink-0">
                <router-link :to="getTallerRoute(taller, true)" class="block w-full h-full">
                  <div class="relative w-full aspect-square">
                    <img :src="taller.imagen" :alt="taller.titulo" class="absolute inset-0 w-full h-full object-cover" />
                  </div>
                </router-link>
              </div>
              <!-- Contenido del taller -->
              <div class="flex flex-col md:flex-row p-2 md:p-4 gap-4 flex-1">
                <!-- Info principal del taller -->
                <div class="min-w-0 flex flex-col justify-center w-full md:w-[200px]">
                  <div class="text-xs text-gray-600 font-semibold uppercase mb-1 tracking-wide">
                    {{ formatearFechaTaller(taller) }}</div>
                  <h3 class="text-2xl font-extrabold text-black mb-4 leading-tight break-words w-full">
                    {{ truncarTituloTaller(taller.titulo) }}
                  </h3>
                  <div class="mt-auto">
                    <span class="text-base font-bold text-black">Docente</span><br>
                    <span class="text-base text-gray-800">{{ taller.docente }}</span>
                  </div>
                </div>
                <!-- Métricas y progreso -->
                <div class="flex flex-col md:flex-row flex-wrap gap-4 flex-1 w-full">
                  <!-- Card Alumnos -->
                  <div class="hidden md:flex bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full md:flex-1 min-w-[180px]">
                    <div class="flex items-center gap-2 mb-2">
                      <span class="bg-blue-100 text-blue-600 rounded-xl p-2 flex items-center justify-center">
                        <img src="@/assets/images/Talleres/alumnos.png" alt="Alumnos" class="w-6 h-6 object-contain" />
                      </span>
                      <div>
                        <div class="text-sm font-bold text-gray-800 leading-tight">Alumnos</div>
                        <div class="text-xs text-gray-500">Participación y actividad</div>
                      </div>
                    </div>
                    <div class="text-2xl font-extrabold text-gray-900 mb-1">
                      {{ formatNumber(obtenerTotalAlumnosTaller(taller)) }}
                    </div>
                    <div class="text-sm text-gray-500 mb-2">Total de alumnos</div>
                    <div class="mt-auto">
                      <div class="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Total de alumnos</span>
                        <span class="font-bold text-black">
                          {{ formatNumber(obtenerTotalAlumnosTaller(taller)) }}
                        </span>
                      </div>
                      <div class="w-full h-2 bg-gray-200 rounded-full">
                        <div class="h-2 bg-green-500 rounded-full"
                          :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.perfiles_completos, totalAlumnosPorTaller[taller.id])}%` }">
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Card Anécdotas -->
                  <div class="hidden md:flex bg-white border border-gray-200 rounded-2xl shadow flex-col justify-between p-4 w-full md:flex-1 min-w-[180px]">
                    <div class="flex items-center gap-2 mb-2">
                      <span class="bg-purple-100 text-purple-600 rounded-xl p-2 flex items-center justify-center">
                        <img src="@/assets/images/Talleres/anecdotas.png" alt="Tareas" class="w-6 h-6 object-contain" />
                      </span>
                      <div>
                        <div class="text-sm font-bold text-gray-800 leading-tight">{{ taller.terminoPlural ?
                          taller.terminoPlural.charAt(0).toUpperCase() + taller.terminoPlural.slice(1) : 'Tareas' }}</div>
                        <div class="text-xs text-gray-500">Contexto y valoraciones</div>
                      </div>
                    </div>
                    <div class="text-2xl font-extrabold text-gray-900 mb-1">
                      {{ formatNumber(totalAnecdotasPorTaller[taller.id] || 0) }}
                    </div>
                    <div class="mt-auto">
                      <div class="flex flex-col gap-2">
                        <template v-if="taller.docente?.toLowerCase().includes('hernan casciari')">
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Con audio</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(estadisticasPorTaller[taller.id]?.cuentos_con_audio || 0) }}
                                <span class="text-gray-400">({{
                                  getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_audio,
                                    totalAnecdotasPorTaller[taller.id]) }}%)</span>
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-blue-500 rounded-full"
                                :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_audio, totalAnecdotasPorTaller[taller.id])}%` }">
                              </div>
                            </div>
                          </div>
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Con resúmenes</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes || 0) }}
                                <span class="text-gray-400">({{
                                  getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes,
                                    totalAnecdotasPorTaller[taller.id]) }}%)</span>
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-purple-500 rounded-full"
                                :style="{ width: `${getPercentageOfTotal(estadisticasPorTaller[taller.id]?.cuentos_con_resumenes, totalAnecdotasPorTaller[taller.id])}%` }">
                              </div>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <div>
                            <div class="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Total de {{ taller.terminoPlural?.toLowerCase() || 'tareas' }}</span>
                              <span class="font-bold text-black">
                                {{ formatNumber(totalAnecdotasPorTaller[taller.id] || 0) }}
                              </span>
                            </div>
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                              <div class="h-2 bg-purple-500 rounded-full"
                                :style="{ width: totalAnecdotasPorTaller[taller.id] > 0 ? '100%' : '0%' }">
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                  <!-- Card de progreso -->
                  <div class="flex flex-col justify-between w-full md:flex-1 items-end">
                    <div class="w-full">
                      <div class="text-sm font-bold text-black mb-1">Progreso</div>
                      <div class="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                        <div class="h-full rounded-full transition-all" 
                             :class="{
                               'bg-[rgba(49,159,67,1)]': taller.progreso === 100 || taller.slug === 'taller-1' || taller.slug === 'taller-2',
                               'bg-green-500': taller.progreso > 0 && taller.progreso < 100,
                               'bg-gray-400': taller.progreso === 0
                             }"
                             :style="{ width: `${taller.progreso}%` }">
                        </div>
                      </div>
                      <div class="text-right mb-4">
                        <span class="text-sm font-bold text-black">
                          {{ getEstadoTaller(taller) }}
                        </span>
                        <div v-if="taller.progreso > 0 && taller.progreso < 100" class="text-xs text-gray-500 mt-0.5">
                          {{ Math.round(taller.progreso) }}% completado
                        </div>
                      </div>
                    </div>
                    <template v-if="mostrarBotonCertificado(taller) && !usuario.value?.docente">
                      <button @click="handleDescargarCertificado(taller)"
                        class="w-full py-2 text-black rounded-lg text-base font-bold flex items-center justify-end transition text-right hover:opacity-80">
                        <span class="flex items-center">
                          <ArrowDownTrayIcon class="mr-2 w-4 h-4" />
                          Certificado
                        </span>
                      </button>
                    </template>
                    <template v-else>
                      <router-link :to="getTallerRoute(taller, true)"
                        class="w-full py-2 text-black rounded-lg text-base font-bold flex items-center justify-end transition text-right hover:opacity-80">
                        <span class="flex items-center">
                          <ChevronRightIcon class="mr-2 w-4 h-4" />
                          Ir al taller
                        </span>
                      </router-link>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile Layout -->
             <div class="flex md:hidden flex-row items-stretch p-4 gap-12">
              <!-- Info principal del taller -->
              <div class="min-w-0 flex flex-col flex-1 justify-evenly">
                <div class="text-black mb-1 tracking-wide text-[10px] leading-[130%] uppercase">
                  <span class="font-inter font-normal">{{ formatNextClassDateMobile(taller).prefix }}</span>
                  <span class="font-inter font-bold ml-1">{{ formatNextClassDateMobile(taller).date }}</span>
                </div>
                <h3 class="font-inter font-bold text-[16px] leading-[120%] tracking-[0%] text-black mb-2 break-words">
                  {{ truncarTituloTaller(taller.titulo) }}
                </h3>
                <div class="font-inter font-normal text-[10px] leading-[130%] tracking-[0%] uppercase text-gray-800">{{ taller.docente }}</div>

                <!-- Estado -->
                <div class="flex items-center">
                  <div class="w-1/4 h-3 rounded-full overflow-hidden flex-shrink-0 bg-gray-200">
                    <div class="h-full rounded-full transition-all"
                         :class="{
                           'bg-[rgba(49,159,67,1)]': taller.progreso === 100 || taller.slug === 'taller-1' || taller.slug === 'taller-2',
                           'bg-green-500': taller.progreso > 0 && taller.progreso < 100,
                           'bg-gray-400': taller.progreso === 0
                         }"
                         :style="{ width: `${taller.progreso}%` }">
                    </div>
                  </div>
                  <div class="ml-2 text-sm font-bold text-black">
                    <span class="font-inter font-bold text-[12px] leading-[132%] tracking-[0%] text-center align-middle">
                      {{ getEstadoTaller(taller) }}
                    </span>
                    <div v-if="taller.progreso > 0 && taller.progreso < 100" class="text-[10px] text-gray-500 mt-0.5">
                      {{ Math.round(taller.progreso) }}% completado
                    </div>
                  </div>
                </div>
              </div>

              <!-- Imagen del taller y Link -->
              <div class="flex flex-col items-end justify-between flex-1">
                 <router-link :to="getTallerRoute(taller, true)" class="block w-24 flex-shrink-0 mb-2">
                   <div class="relative w-full aspect-square rounded-md overflow-hidden">
                     <img :src="taller.imagen" :alt="taller.titulo" class="absolute inset-0 w-full h-full object-cover" />
                   </div>
                 </router-link>
                 <template v-if="mostrarBotonCertificado(taller) && !usuario.value?.docente">
                   <button @click="handleDescargarCertificado(taller)"
                     class="font-inter font-bold text-xs leading-[140%] tracking-[0%] text-black flex items-center whitespace-nowrap hover:opacity-80">
                     <span class="flex items-center">
                       Certificado
                       <ArrowDownTrayIcon class="ml-1 w-3 h-3" />
                     </span>
                   </button>
                 </template>
                 <template v-else>
                   <router-link :to="getTallerRoute(taller, true)"
                     class="font-inter font-bold text-xs leading-[140%] tracking-[0%] text-black flex items-center whitespace-nowrap hover:opacity-80">
                     <span class="flex items-center">
                       Ir al taller
                       <ChevronRightIcon class="ml-1 w-3 h-3" />
                     </span>
                   </router-link>
                 </template>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Mensaje si no hay talleres -->
      <div v-if="!isLoadingTalleres && ((esDocente && talleresComoAlumno.length === 0) || (!esDocente && (!talleres || talleres.length === 0)))" class="text-center py-12 px-4">
        <div class="max-w-xl mx-auto mt-4">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 class="mt-2 text-lg font-medium text-gray-900">Aún no estás inscripto a ningún taller como alumno</h3>
          <p class="mt-1 text-sm text-gray-500">Explorá los talleres disponibles y encontrá el que mejor se adapte a vos.</p>
        </div>
      </div>

      <hr class="my-8 border-gray-200" />
    </template>

    <!-- Sección de novedad para usuarios no logueados -->
    <template v-else>
      <!-- Loading state para novedad -->
      <div v-if="isLoadingNovedad" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>

      <!-- Novedad de la comunidad -->
      <div v-else-if="novedad" class="mb-12 mt-8">
        <div class="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
          <!-- Desktop Layout -->
          <div class="hidden md:flex h-auto">
            <!-- Imagen -->
            <div class="w-1/2 flex-shrink-0">
              <div class="relative w-full h-full">
                <img 
                  :src="novedad.featuredImage" 
                  :alt="novedad.title"
                  class="absolute inset-0 w-full h-full object-cover"
                  @error="$event.target.style.display='none'"
                />
                <div v-if="!novedad.featuredImage" class="absolute inset-0 bg-gray-100 flex items-center justify-center">
                  <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Contenido -->
            <div class="flex-1 p-6 flex flex-col justify-between">
              <div>
                <div class="flex items-center gap-2 mb-3">
                  <span class="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                    Novedad
                  </span>
                  <span class="text-sm text-gray-500">
                    {{ formatearFechaNovedad(novedad.date) }}
                  </span>
                </div>
                
                <h3 class="font-inter font-bold text-[16px] leading-[128%] tracking-[0%] md:text-[24px] md:leading-[120%] text-gray-900 mb-3 leading-tight hover:text-blue-600 transition-colors">
                  {{ novedad.title }}
                </h3>
                
                <p class="text-gray-700 leading-relaxed line-clamp-3">
                  {{ stripHtml(novedad.excerpt) }}
                </p>

              </div>
              
              <div class="mt-6">
                <a :href="novedad.linkPersonalizado || novedad.link" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   class="inline-flex items-center gap-2 bg-gray-900 text-sm text-white px-3 py-2 rounded-lg font-semibold hover:bg-gray-800 transition-colors">
                  Leer más
                  <ChevronRightIcon class="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <!-- Mobile Layout -->
          <div class="md:hidden">
            <!-- Imagen -->
            <div class="w-full">
              <div class="relative w-full h-56">
                <img 
                  :src="novedad.featuredImage" 
                  :alt="novedad.title"
                  class="absolute inset-0 w-full h-full object-cover"
                  @error="$event.target.style.display='none'"
                />
                <div v-if="!novedad.featuredImage" class="absolute inset-0 bg-gray-100 flex items-center justify-center">
                  <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Contenido -->
            <div class="p-4">
              <div class="flex items-center gap-2 mb-3">
                <span class="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Novedad
                </span>
                <span class="text-xs text-gray-500">
                  {{ formatearFechaNovedad(novedad.date) }}
                </span>
              </div>
              
              <h3 class="font-inter font-bold text-[16px] leading-[128%] tracking-[0%] md:text-[24px] md:leading-[120%] text-gray-900 mb-3 leading-tight hover:text-blue-600 transition-colors">
                {{ novedad.title }}
              </h3>
              
              <p class="text-gray-700 text-sm leading-relaxed mb-3 line-clamp-2">
                {{ stripHtml(novedad.excerpt) }}
              </p>
              
              <div class="flex items-center justify-end">
                <a :href="novedad.linkPersonalizado || novedad.link" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   class="inline-flex items-center gap-1 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors">
                  Leer más
                  <ChevronRightIcon class="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Bloque de todos los talleres para comprar -->
    <div class="mt-8">
      <Talleres />
    </div>

  </div>
</div>
</template>

<style lang="scss">
.fade {

&-enter-active,
&-leave-active {
  transition: all 0.5s ease;
}

&-enter-from,
&-leave-to {
  opacity: 0;
}

&-enter-to,
&-leave-from {
  opacity: 1;
}
}

/* Ajustes responsive */
@media (max-width: 768px) {
.grid {
  gap: 1rem;
}

.p-4 {
  padding: 0.75rem;
}
}

/* Agregar la fuente Inter */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

.font-inter {
font-family: 'Inter', sans-serif;
}

/* Line clamp utilities */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

<style>
.aspect-square {
aspect-ratio: 1 / 1;
}
</style>